import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider, Snackbar, Button, Alert } from '@mui/material';

import AppRoutes from './routes/AppRoutes';
import NavigationScroll from './layout/NavigationScroll';
import Theme from './themes';
import { ButtonStyles } from './utils/general';
import { useTheme } from "@mui/styles";
import ScrapingProgressModal from './Components/ScrapingProgressModal';
import LinkedinScrapeProgressModal from './Components/LinkedinScrapeProgressModal';

// Configuration
const INACTIVITY_LIMIT_MINS = 180;

const App = ({ newVersionAvailable }) => {
    const Customization = useSelector((state) => state.customization);
    const [showUpdateBanner, setShowUpdateBanner] = useState(newVersionAvailable);
    const [showSessionWarning, setShowSessionWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);
    const theme = useTheme();

    const timeoutRef = useRef(null);

    const handleRefresh = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload(true);
    };

    const handleSessionLogout = useCallback(() => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("tokenExpiry");
        sessionStorage.clear();
        window.location.href = "/login";
    }, []);

    const resetInactivityTimer = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            handleSessionLogout();
        }, INACTIVITY_LIMIT_MINS * 60 * 1000);
    }, [handleSessionLogout]);

    useEffect(() => {
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

        events.forEach(event => window.addEventListener(event, resetInactivityTimer));

        resetInactivityTimer();

        return () => {
            events.forEach(event => window.removeEventListener(event, resetInactivityTimer));
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [resetInactivityTimer]);

    useEffect(() => {
        const checkInterval = setInterval(() => {
            if (localStorage.getItem('new_version_detected') === 'true') {
                setShowUpdateBanner(true);
            }
        }, 5000);
        return () => clearInterval(checkInterval);
    }, []);

    useEffect(() => {
        const expiry = localStorage.getItem("tokenExpiry");
        if (!expiry) return;

        const interval = setInterval(() => {
            const remaining = Number(expiry) - Date.now();

            if (remaining <= 0) {
                handleSessionLogout();
                clearInterval(interval);
                return;
            }

            setTimeLeft(remaining);

            if (remaining <= 60 * 60 * 1000 && !showSessionWarning) {
                setShowSessionWarning(true);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [handleSessionLogout, showSessionWarning]);

    const formatTime = (ms) => {
        if (!ms) return "Loading...";
        const totalSec = Math.floor(ms / 1000);
        const hrs = Math.floor(totalSec / 3600);
        const mins = Math.floor((totalSec % 3600) / 60);
        const secs = totalSec % 60;
        return `${hrs}h ${mins}m ${secs}s`;
    };

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={Theme(Customization)}>
                <CssBaseline />
                <Snackbar
                    open={showUpdateBanner}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        severity="info"
                        sx={{ width: '100%', backgroundColor: '#fff', color: '#1976d2' }}
                        action={
                            <Button
                                size="small"
                                onClick={handleRefresh}
                                variant="contained"                           color="primary"
                            >
                                Refresh
                            </Button>
                        }
                    >
                        A new version of the app is available.
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={showSessionWarning}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        severity="warning"
                        sx={{ width: '100%', backgroundColor: '#fff3cd', color: '#856404' }}
                        action={
                            <Button
                                size="small"
                                onClick={() => setShowSessionWarning(false)}
                                variant="contained"                           color="primary"
                            >
                                Hide
                            </Button>
                        }
                    >
                         Your session is about to expire! Time Left: <strong>{formatTime(timeLeft)}</strong> <br/><br/>
                        Please logout and login again, or it will automatically logout.
                    </Alert>
                </Snackbar>

                <NavigationScroll>
                    <ScrapingProgressModal />
                    <LinkedinScrapeProgressModal />
                    <AppRoutes />
                </NavigationScroll>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default App;